// UltraFlex Admin Portal - E-commerce Orders
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { 
  ShoppingBag, Package, Truck, CheckCircle, XCircle, Clock,
  ChevronLeft, ChevronRight, Filter, Download, Search,
  DollarSign, Eye, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, page, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'orders') {
        const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
        if (statusFilter) params.append('status', statusFilter);
        
        const res = await fetch(`${API_URL}/api/orders?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setOrders(data.items || []);
        setTotalPages(data.total_pages || 1);
      } else if (activeTab === 'products') {
        const res = await fetch(`${API_URL}/api/products?page=${page}&page_size=20`);
        const data = await res.json();
        setProducts(data.items || []);
        setTotalPages(data.total_pages || 1);
      }

      // Fetch revenue stats
      const statsRes = await fetch(`${API_URL}/api/reports/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch orders data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/orders/${orderId}/status?status=${newStatus}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      setSelectedOrder(null);
    } catch (error) {
      alert('Failed to update order status');
    }
  };

  const refundOrder = async (orderId) => {
    const reason = prompt('Enter refund reason:');
    if (!reason) return;

    try {
      await fetch(`${API_URL}/api/orders/${orderId}/refund?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Refund processed');
      fetchData();
    } catch (error) {
      alert('Failed to process refund');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'amber', icon: Clock, label: 'Pending' },
      created: { color: 'gray', icon: Clock, label: 'Created' },
      paid: { color: 'green', icon: CheckCircle, label: 'Paid' },
      processing: { color: 'yellow', icon: Package, label: 'Processing' },
      fulfilled: { color: 'blue', icon: Package, label: 'Fulfilled' },
      shipped: { color: 'purple', icon: Truck, label: 'Shipped' },
      delivered: { color: 'green', icon: CheckCircle, label: 'Delivered' },
      cancelled: { color: 'red', icon: XCircle, label: 'Cancelled' },
      refunded: { color: 'orange', icon: RefreshCw, label: 'Refunded' }
    };
    
    const c = config[status] || { color: 'gray', icon: Clock, label: status };
    const Icon = c.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${c.color}-500/20 text-${c.color}-400`}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-uf-black">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 md:px-8 py-4 md:py-6">
        <h1 className="text-2xl font-bold text-uf-text">Shop & Orders</h1>
        <p className="text-uf-text-muted mt-1">Manage e-commerce orders and products</p>
      </div>

      {/* Stats Cards */}
      <div className="px-4 md:px-8 py-4 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Revenue" 
          value={`£${stats?.ecommerce_revenue?.toLocaleString() || 0}`}
          icon={DollarSign}
          color="green"
        />
        <StatCard 
          title="Orders (30d)" 
          value={stats?.ecommerce_orders || 0}
          icon={ShoppingBag}
          color="blue"
        />
        <StatCard 
          title="Products" 
          value={products.length || '-'}
          icon={Package}
          color="purple"
        />
        <StatCard 
          title="Pending Shipments" 
          value={orders.filter(o => o.status === 'paid' || o.status === 'processing').length}
          icon={Truck}
          color="orange"
        />
      </div>

      {/* Tabs */}
      <div className="px-8 border-b border-uf-border">
        <div className="flex space-x-8">
          {[
            { id: 'orders', label: 'Orders' },
            { id: 'products', label: 'Products' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); setStatusFilter(''); }}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-uf-red text-uf-red' 
                  : 'border-transparent text-uf-text-muted hover:text-uf-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-8 py-4 bg-uf-surface-hi border-b border-uf-border flex items-center gap-4">
        <Filter className="w-5 h-5 text-uf-text-muted" />
        
        {activeTab === 'orders' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="created">Created</option>
            <option value="paid">Paid</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        )}

        <button className="ml-auto flex items-center px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text hover:bg-uf-surface-hi">
          <Download className="w-4 h-4 mr-2" />
          Export
        </button>
      </div>

      {/* Content */}
      <div className="p-4 md:p-8">
        <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
            </div>
          ) : activeTab === 'orders' ? (
            <OrdersTable 
              orders={orders} 
              getStatusBadge={getStatusBadge}
              onViewOrder={setSelectedOrder}
            />
          ) : (
            <ProductsTable products={products} />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-uf-surface-hi border-t border-uf-border flex items-center justify-between">
              <span className="text-uf-text-muted text-sm">Page {page} of {totalPages}</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal 
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={updateOrderStatus}
          onRefund={refundOrder}
          getStatusBadge={getStatusBadge}
        />
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-uf-surface rounded-lg p-5 border border-uf-border">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-uf-text-muted text-sm">{title}</p>
        <p className="text-2xl font-bold text-uf-text mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

const OrdersTable = ({ orders, getStatusBadge, onViewOrder }) => (
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Order #</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Customer</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Items</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Total</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Status</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Date</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {orders.length === 0 ? (
        <tr>
          <td colSpan="7" className="px-6 py-12 text-center text-uf-text-muted">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            No orders found
          </td>
        </tr>
      ) : (
        orders.map((order) => (
          <tr key={order.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4 font-mono text-sm text-uf-red">{order.order_number}</td>
            <td className="px-6 py-4 text-uf-text">{order.member_id ? 'Member' : order.guest_email}</td>
            <td className="px-6 py-4 text-uf-text-muted">{order.items?.length || 0} items</td>
            <td className="px-6 py-4 text-right text-uf-text font-medium">£{order.total?.toFixed(2)}</td>
            <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
            <td className="px-6 py-4 text-uf-text-muted text-sm">
              {new Date(order.created_at).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 text-right">
              <button 
                onClick={() => onViewOrder(order)}
                className="text-uf-red hover:text-uf-red-hover"
              >
                <Eye className="w-4 h-4" />
              </button>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

const ProductsTable = ({ products }) => (
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Product</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">SKU</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Category</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Price</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Stock</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Status</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {products.length === 0 ? (
        <tr>
          <td colSpan="6" className="px-6 py-12 text-center text-uf-text-muted">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            No products found
          </td>
        </tr>
      ) : (
        products.map((product) => (
          <tr key={product.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4">
              <div className="flex items-center">
                {product.images?.[0] && (
                  <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover mr-3" />
                )}
                <span className="text-uf-text">{product.name}</span>
              </div>
            </td>
            <td className="px-6 py-4 font-mono text-sm text-uf-text-muted">{product.sku}</td>
            <td className="px-6 py-4 text-uf-text-muted">{product.category}</td>
            <td className="px-6 py-4 text-right text-uf-text font-medium">£{product.price?.toFixed(2)}</td>
            <td className="px-6 py-4 text-right text-uf-text-muted">
              {product.stock_quantity !== null ? product.stock_quantity : '∞'}
            </td>
            <td className="px-6 py-4">
              <span className={`px-2 py-1 rounded text-xs ${product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {product.is_active ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

const OrderDetailModal = ({ order, onClose, onUpdateStatus, onRefund, getStatusBadge }) => {
  const statusFlow = ['created', 'paid', 'processing', 'shipped', 'delivered'];
  const currentIndex = statusFlow.indexOf(order.status);
  const nextStatus = statusFlow[currentIndex + 1];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-uf-surface rounded-lg border border-uf-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-uf-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-uf-text">Order {order.order_number}</h2>
            <p className="text-uf-text-muted text-sm">
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Status */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted mb-2">Status</h3>
            {getStatusBadge(order.status)}
          </div>

          {/* Items */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted mb-2">Items</h3>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-uf-surface-hi rounded p-3">
                  <div>
                    <p className="text-uf-text">{item.product_name}</p>
                    <p className="text-uf-text-muted text-sm">
                      {item.size && `Size: ${item.size} · `}
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p className="text-uf-text font-medium">£{item.total?.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="mb-6 bg-uf-surface-hi rounded p-4">
            <div className="flex justify-between text-uf-text-muted mb-2">
              <span>Subtotal</span>
              <span>£{order.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-uf-text-muted mb-2">
              <span>Tax</span>
              <span>£{order.tax_total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-uf-text font-bold border-t border-uf-border pt-2">
              <span>Total</span>
              <span>£{order.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Shipping */}
          <div>
            <h3 className="text-sm font-semibold text-uf-text-muted mb-2">Shipping Address</h3>
            <p className="text-uf-text">
              {order.shipping_address}<br/>
              {order.shipping_city}, {order.shipping_postcode}<br/>
              {order.shipping_phone}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-uf-border flex justify-end space-x-3">
          {nextStatus && !['cancelled', 'refunded'].includes(order.status) && (
            <PermissionGate perm="shop.fulfil">
              <button
                onClick={() => onUpdateStatus(order.id, nextStatus)}
                className="px-4 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover"
              >
                Mark as {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
              </button>
            </PermissionGate>
          )}
          {['paid', 'fulfilled', 'shipped', 'delivered'].includes(order.status) && (
            <PermissionGate perm="shop.refund">
              <button
                onClick={() => onRefund(order.id)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Refund
              </button>
            </PermissionGate>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
